import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Search,
  Laptop,
  ArrowRight,
  Sparkles,
  Loader2,
  CheckCircle2,
  Clock,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import trustMarkBadge from "@/assets/Trusted.webp";
import dpoDpsMark from "@/assets/dpo-dps-mark.webp";
import { useDemoRequest } from "@/hooks/email/useDemoRequest";
import ThankYouPage from "@/components/ThankYouPage";
import TurnstileWidget from "@/components/shared/TurnstileWidget";
import TimePicker from "@/components/shared/TimePicker";
import { toast } from "sonner";
import { z } from "zod";

/**
 * Demo request modal with multi-step validation and Turnstile captcha.
 */

// --- Advanced Framer Motion Variants ---
const modalReveal = {
  hidden: {
    opacity: 0,
    scale: 0.98,
  },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
      delayChildren: 0.1,
      // Increased stagger time from 0.35 to 0.45.
      // This forces the right panel to wait a little longer,
      // letting the left panel's vertical slide be the star of the show first.
      staggerChildren: 0.45,
    },
  },
};

const leftPanelReveal = {
  // Increased 'y' distance from 150 to 400 for a much larger, more visible travel distance
  hidden: { opacity: 0, y: 400, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    // Lowered stiffness and damping for a slightly slower, more sweeping entrance
    transition: { type: "spring", stiffness: 120, damping: 20 },
  },
};

const rightPanelReveal = {
  // Increased 'x' distance so it matches the dramatic feel of the left panel
  hidden: { opacity: 0, x: 250, filter: "blur(10px)" },
  show: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 150, damping: 22 },
  },
};

const formContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const formItem = {
  hidden: { opacity: 0, x: 20, y: 10, filter: "blur(4px)" },
  show: {
    opacity: 1,
    x: 0,
    y: 0,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 280, damping: 22 },
  },
};

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateCompact = (dateStr) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const MIN_PREFERRED_TIME = "10:00";
const MAX_PREFERRED_TIME = "20:00";
const MIN_PREFERRED_TIME_DISPLAY = "10:00 AM";
const MAX_PREFERRED_TIME_DISPLAY = "08:00 PM";

const to24HourTime = (timeStr) => {
  if (!timeStr) return "";

  const already24Hour = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (already24Hour) return timeStr;

  const matched = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!matched) return "";

  let [, hour, minute, period] = matched;
  let hourNumber = Number(hour);

  if (period.toUpperCase() === "AM") {
    if (hourNumber === 12) hourNumber = 0;
  } else if (hourNumber !== 12) {
    hourNumber += 12;
  }

  return `${String(hourNumber).padStart(2, "0")}:${minute}`;
};

const isTimeWithinRange = (timeStr) => {
  const converted = to24HourTime(timeStr);
  if (!converted) return false;
  return converted >= MIN_PREFERRED_TIME && converted <= MAX_PREFERRED_TIME;
};

const isValidEmail = (email) => {
  const normalized = email.trim();
  if (!normalized) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
};

const preferredTimeSchema = z
  .string()
  .min(1, "Please select your preferred demo time.")
  .refine(
    (value) => isTimeWithinRange(value),
    "Preferred time must be between 10:00 AM and 8:00 PM (PH/Manila time)."
  );

export default function DemoModal({ trigger, open, onOpenChange }) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [timeValidationError, setTimeValidationError] = useState("");
  const isTurnstileEnabled = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState({
    hasSoftware: "",
    currentSoftwareName: "",
    firstName: "",
    lastName: "",
    clinicName: "",
    clinicAddress: "",
    contactNumber: "",
    email: "",
    businessType: "",
    howDidYouHear: "",
    preferredDate: "",
    preferredTime: "",
  });
  const startOfToday = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate()
  );
  const currentYear = startOfToday.getFullYear();
  const calendarStartMonth = new Date(currentYear, 0, 1);
  const calendarEndMonth = new Date(currentYear + 20, 11, 1);

  const { mutate: submitDemoRequest, isPending } = useDemoRequest();

  useEffect(() => {
    if (open) {
      document.body.classList.add("demo-modal-open");
      setShowSuccess(false);
    } else {
      document.body.classList.remove("demo-modal-open");
    }
    return () => document.body.classList.remove("demo-modal-open");
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "contactNumber") {
      const numericValue = value.replace(/\D/g, "").slice(0, 11);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const timeValidation = preferredTimeSchema.safeParse(formData.preferredTime);
    if (!timeValidation.success) {
      setTimeValidationError(timeValidation.error.issues[0]?.message || "Invalid time selected.");
      return;
    }

    setTimeValidationError("");

    if (isTurnstileEnabled && !turnstileToken) {
      toast.error("Captcha verification failed. Please try again.");
      return;
    }

    submitDemoRequest(
      { ...formData, preferredTime: to24HourTime(formData.preferredTime), turnstileToken },
      {
        onSuccess: () => {
          if (onOpenChange) onOpenChange(false);
          setTurnstileToken("");
          setTurnstileResetKey((prev) => prev + 1);
          turnstileRef.current?.reset?.();
          setShowSuccess(true);
        },
      }
    );
  };

  const handleClearForm = () => {
    setFormData({
      hasSoftware: "",
      currentSoftwareName: "",
      firstName: "",
      lastName: "",
      clinicName: "",
      clinicAddress: "",
      contactNumber: "",
      email: "",
      businessType: "",
      howDidYouHear: "",
      preferredDate: "",
      preferredTime: "",
    });
    setCurrentStep(1);
    setTurnstileToken("");
    setTimeValidationError("");
    setTurnstileResetKey((prev) => prev + 1);
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    handleClearForm();
  };

  useEffect(() => {
    if (!open) return;
    setCurrentStep(1);
    setTurnstileToken("");
    setTimeValidationError("");
    setTurnstileResetKey((prev) => prev + 1);
  }, [open]);

  const handlePreferredTimeChange = (newTime) => {
    setFormData((prev) => ({ ...prev, preferredTime: newTime }));

    const timeValidation = preferredTimeSchema.safeParse(newTime);
    if (!timeValidation.success) {
      setTimeValidationError(timeValidation.error.issues[0]?.message || "Invalid time selected.");
      return;
    }

    setTimeValidationError("");
  };

  const isSoftwareComplete =
    formData.hasSoftware === "no" ||
    (formData.hasSoftware === "yes" && formData.currentSoftwareName.trim() !== "");
  const hasValidEmail = isValidEmail(formData.email);
  const isPersonalInfoComplete =
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.contactNumber.trim() !== "" &&
    hasValidEmail;
  const isClinicInfoComplete =
    formData.clinicName.trim() !== "" && formData.clinicAddress.trim() !== "";
  const isScheduleComplete =
    formData.preferredDate !== "" && isTimeWithinRange(formData.preferredTime);

  const canProceedByStep = {
    1: isSoftwareComplete,
    2: isPersonalInfoComplete,
    3: isClinicInfoComplete,
    4: isScheduleComplete,
  };

  const handleNextStep = () => {
    if (currentStep >= 4) return;
    if (!canProceedByStep[currentStep]) return;
    setCurrentStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    if (currentStep <= 1) return;
    setCurrentStep((prev) => prev - 1);
  };

  const labelClass =
    "mb-2 block text-sm sm:text-base font-bold tracking-wider text-slate-500 uppercase";
  const innerWrapperClass = "relative group flex w-full min-w-0 items-center";
  const inputClass =
    "w-full min-w-0 truncate rounded-xl border border-slate-200 bg-slate-50/50 py-2.5 pr-4 pl-12 text-base font-medium text-slate-900 placeholder:text-sm placeholder:text-slate-400 transition-all duration-300 focus:border-blue-600 focus:bg-white focus:ring-[4px] focus:ring-blue-600/10 focus:outline-none hover:border-slate-300 hover:bg-slate-50 sm:py-4 sm:text-lg sm:placeholder:text-base";
  const iconClass =
    "pointer-events-none absolute top-1/2 left-4 z-10 -translate-y-1/2 text-slate-400 transition-colors duration-300 group-hover:text-slate-500 group-focus-within:text-blue-600";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

        <DialogContent
          style={{ perspective: "1500px" }}
          className="h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] overflow-hidden border-none bg-transparent p-0 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.4)] data-[state=closed]:animate-none data-[state=open]:animate-none sm:h-[90vh] sm:max-h-[90vh] sm:w-full sm:max-w-[calc(100vw-2rem)] sm:rounded-[2rem] md:max-w-5xl">
          <motion.div
            variants={modalReveal}
            initial="hidden"
            animate="show"
            className="flex h-full w-full flex-col overflow-y-auto bg-white sm:rounded-[inherit] md:flex-row md:overflow-hidden">
            {/* LEFT PANEL */}
            <motion.div
              variants={leftPanelReveal}
              className="relative hidden w-[42%] shrink-0 flex-col justify-between overflow-hidden bg-[#0A102E] p-6 text-white md:flex lg:p-7">
              <div className="pointer-events-none absolute -top-[10%] -left-[20%] h-125 w-125 rounded-full bg-blue-600/20 blur-[120px]" />
              <div className="pointer-events-none absolute -right-[20%] -bottom-[10%] h-100 w-100 rounded-full bg-indigo-600/20 blur-[100px]" />

              <div className="relative z-10">
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-blue-400 shadow-inner backdrop-blur-md">
                  <Sparkles size={20} strokeWidth={1.5} />
                </div>
                <h1 className="mb-2 text-[1.9rem] leading-[1.12] font-extrabold tracking-tight lg:text-[2.25rem]">
                  Modernize Your <br />
                  <span className="bg-linear-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                    Veterinary Practice
                  </span>
                </h1>
                {/* INCREASED VISIBILITY: text-slate-300 changed to text-slate-100 */}
                <p className="pr-2 text-sm leading-relaxed text-slate-100 sm:text-base">
                  Experience how VetAssist streamlines workflows, saving your team time and
                  improving patient care.
                </p>

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 rounded-full bg-blue-500/10 p-2 text-blue-400">
                      <Clock size={18} strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white lg:text-base">
                        Save Hours Daily
                      </h3>
                      {/* INCREASED VISIBILITY: text-slate-400 changed to text-slate-200 */}
                      <p className="mt-1 text-sm text-slate-200">
                        Automate scheduling and records.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 rounded-full bg-indigo-500/10 p-2 text-indigo-400">
                      <ShieldCheck size={18} strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white lg:text-base">Secure Data</h3>
                      {/* INCREASED VISIBILITY: text-slate-400 changed to text-slate-200 */}
                      <p className="mt-1 text-sm text-slate-200">
                        Enterprise-grade security for your clinic.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 rounded-full bg-emerald-500/10 p-2 text-emerald-400">
                      <CheckCircle2 size={18} strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white lg:text-base">
                        Easy Migration
                      </h3>
                      {/* INCREASED VISIBILITY: text-slate-400 changed to text-slate-200 */}
                      <p className="mt-1 text-sm text-slate-200">
                        We help you move from your old software.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative z-10 mt-auto pt-4">
                {/* INCREASED VISIBILITY: text-slate-500 changed to text-slate-300 */}
                <p className="mb-2 text-[10px] font-bold tracking-widest text-slate-300 uppercase lg:text-xs">
                  Trusted & Certified
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-28 items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-white p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] backdrop-blur-md transition-all duration-300 lg:h-14 lg:w-32 lg:p-2">
                    <img
                      src={trustMarkBadge}
                      alt="TrustMark"
                      className="h-full w-full object-contain opacity-95 saturate-110"
                    />
                  </div>
                  <div className="flex h-12 w-28 items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-white p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] backdrop-blur-md transition-all duration-300 lg:h-14 lg:w-32 lg:p-2">
                    <img
                      src={dpoDpsMark}
                      alt="DPO/DPS"
                      className="h-full w-full object-contain opacity-95 saturate-110"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* RIGHT PANEL - Form Area (Unchanged) */}
            <motion.div
              variants={rightPanelReveal}
              className="flex w-full min-w-0 flex-col overflow-hidden bg-white md:w-[58%]">
              <motion.div
                variants={formContainer}
                initial="hidden"
                animate="show"
                className="scrollbar-thin scrollbar-thumb-slate-200 relative flex-1 overflow-y-auto px-4 pt-6 pb-4 sm:p-8 lg:p-12">
                <div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                    Your Details
                  </h2>
                  <span className="inline-flex w-fit items-center gap-2 rounded-full bg-rose-50 px-3 py-1.5 text-xs font-bold tracking-wider text-rose-500 uppercase">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500"></span>
                    Required Fields
                  </span>
                </div>

                <div className="mb-4 flex gap-2 sm:mb-8">
                  <div
                    className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 1 ? "bg-blue-600" : "bg-slate-200"}`}
                  />
                  <div
                    className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 2 ? "bg-blue-600" : "bg-slate-200"}`}
                  />
                  <div
                    className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 3 ? "bg-blue-600" : "bg-slate-200"}`}
                  />
                  <div
                    className={`h-2 flex-1 rounded-full transition-colors duration-500 ${currentStep >= 4 ? "bg-blue-600" : "bg-slate-200"}`}
                  />
                </div>

                <div className="mb-4 text-sm font-semibold text-blue-700 sm:mb-6">
                  Step {currentStep} of 4
                </div>

                <form id="demo-form" onSubmit={handleSubmit} className="space-y-6 sm:space-y-12">
                  {currentStep === 1 && (
                    <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                      <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                        1. Software Details
                      </h3>
                      <div className="w-full">
                        <label className={labelClass}>
                          Current Software Status <span className="text-rose-500">*</span>
                        </label>
                        <div className="group relative w-full min-w-0">
                          <Laptop size={20} className={iconClass} />
                          <Select
                            value={formData.hasSoftware}
                            onValueChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                hasSoftware: value,
                                currentSoftwareName:
                                  value === "yes" ? prev.currentSoftwareName : "",
                              }))
                            }>
                            <SelectTrigger className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50/50 pr-4 pl-12 text-left text-base font-medium text-slate-900 shadow-none transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-blue-600/10 sm:h-14 sm:py-8 sm:text-lg">
                              <SelectValue placeholder="Select your current status..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes" className="py-2 text-base sm:py-2 sm:text-lg">
                                Yes, we currently use a software
                              </SelectItem>
                              <SelectItem value="no" className="py-2 text-base sm:py-2 sm:text-lg">
                                No, we are not using any software
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <AnimatePresence mode="popLayout">
                        {formData.hasSoftware === "yes" && (
                          <motion.div
                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, height: "auto", marginTop: 24 }}
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            className="w-full overflow-hidden">
                            <label className={labelClass}>
                              What software are you using? <span className="text-rose-500">*</span>
                            </label>
                            <div className={innerWrapperClass}>
                              <Search size={20} className={iconClass} />
                              <input
                                type="text"
                                name="currentSoftwareName"
                                value={formData.currentSoftwareName}
                                onChange={handleInputChange}
                                placeholder="e.g., Hippo Manager, Neo"
                                required={formData.hasSoftware === "yes"}
                                className={inputClass}
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}

                  {currentStep === 2 && (
                    <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                      <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                        2. Personal Information
                      </h3>
                      <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-8">
                        <div className="w-full">
                          <label className={labelClass}>
                            First Name <span className="text-rose-500">*</span>
                          </label>
                          <div className={innerWrapperClass}>
                            <User size={20} className={iconClass} />
                            <input
                              type="text"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              placeholder="Jane"
                              required
                              className={inputClass}
                            />
                          </div>
                        </div>

                        <div className="w-full">
                          <label className={labelClass}>
                            Last Name <span className="text-rose-500">*</span>
                          </label>
                          <div className={innerWrapperClass}>
                            <User size={20} className={iconClass} />
                            <input
                              type="text"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              placeholder="Doe"
                              required
                              className={inputClass}
                            />
                          </div>
                        </div>

                        <div className="w-full">
                          <label className={labelClass}>
                            Contact Number <span className="text-rose-500">*</span>
                          </label>
                          <div className={innerWrapperClass}>
                            <Phone size={20} className={iconClass} />
                            <input
                              type="tel"
                              name="contactNumber"
                              value={formData.contactNumber}
                              onChange={handleInputChange}
                              placeholder="09123456789"
                              maxLength={11}
                              required
                              className={inputClass}
                            />
                          </div>
                        </div>

                        <div className="w-full">
                          <label className={labelClass}>
                            Email Address <span className="text-rose-500">*</span>
                          </label>
                          <div className={innerWrapperClass}>
                            <Mail size={20} className={iconClass} />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="jane@clinic.com"
                              required
                              className={inputClass}
                            />
                          </div>
                          {formData.email.trim() !== "" && !hasValidEmail && (
                            <p className="mt-2 text-xs text-rose-500">
                              Please enter a valid email address.
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 3 && (
                    <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                      <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                        3. Clinic Information
                      </h3>
                      <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-8">
                        <div className="w-full sm:col-span-2">
                          <label className={labelClass}>
                            Clinic Name <span className="text-rose-500">*</span>
                          </label>
                          <div className={innerWrapperClass}>
                            <Building2 size={20} className={iconClass} />
                            <input
                              type="text"
                              name="clinicName"
                              value={formData.clinicName}
                              onChange={handleInputChange}
                              placeholder="Happy Paws Veterinary"
                              required
                              className={inputClass}
                            />
                          </div>
                        </div>

                        <div className="w-full sm:col-span-2">
                          <label className={labelClass}>
                            Clinic Address <span className="text-rose-500">*</span>
                          </label>
                          <div className={innerWrapperClass}>
                            <MapPin size={20} className={iconClass} />
                            <input
                              type="text"
                              name="clinicAddress"
                              value={formData.clinicAddress}
                              onChange={handleInputChange}
                              placeholder="123 Vet Street, City"
                              required
                              className={inputClass}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {currentStep === 4 && (
                    <motion.div variants={formItem} className="space-y-4 sm:space-y-6">
                      <h3 className="border-b border-slate-100 pb-2 text-xl font-bold text-slate-800">
                        4. Preferred Demo Schedule
                      </h3>
                      <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-8">
                        <div className="w-full">
                          <label className={labelClass}>Preferred Date</label>
                          <div className={innerWrapperClass}>
                            <Calendar size={20} className={iconClass} />
                            <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-11 w-full justify-start rounded-xl border border-slate-200 bg-slate-50/50 pr-4 pl-12 text-left text-sm font-medium text-stone-500 shadow-none transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-blue-600/10 sm:h-12 sm:text-base">
                                  {formData.preferredDate
                                    ? formatDateCompact(formData.preferredDate)
                                    : "Select date"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-auto p-0">
                                <CalendarPicker
                                  mode="single"
                                  selected={
                                    formData.preferredDate
                                      ? new Date(`${formData.preferredDate}T00:00:00`)
                                      : undefined
                                  }
                                  fromDate={startOfToday}
                                  startMonth={calendarStartMonth}
                                  endMonth={calendarEndMonth}
                                  onSelect={(date) => {
                                    if (!date) return;
                                    setFormData((prev) => ({
                                      ...prev,
                                      preferredDate: toDateInputValue(date),
                                    }));
                                    setIsDatePopoverOpen(false);
                                  }}
                                  disabled={(date) => date < startOfToday}
                                  captionLayout="dropdown"
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                        <div className="w-full">
                          <label className={labelClass}>Preferred Time</label>
                          <div
                            className={innerWrapperClass}
                            style={{ pointerEvents: timeValidationError ? "none" : "auto" }}>
                            <Clock size={20} className={iconClass} />
                            <div className="w-full min-w-0">
                              <TimePicker
                                value={formData.preferredTime}
                                onChange={handlePreferredTimeChange}
                                placeholder="Select time"
                                minTime={MIN_PREFERRED_TIME_DISPLAY}
                                maxTime={MAX_PREFERRED_TIME_DISPLAY}
                              />
                            </div>
                          </div>
                          {timeValidationError ? (
                            <p className="mt-2 text-xs text-rose-500">{timeValidationError}</p>
                          ) : (
                            <p className="mt-2 text-xs text-slate-500">
                              Preferred time is available from 10:00 AM to 8:00 PM (PH/Manila time).
                            </p>
                          )}
                        </div>
                      </div>

                      {isTurnstileEnabled && (
                        <div className="mt-4 flex flex-col items-start gap-3">
                          <TurnstileWidget
                            ref={turnstileRef}
                            className="w-full max-w-[320px]"
                            action="demo_request"
                            mode="managed"
                            requireManualInteraction
                            resetKey={turnstileResetKey}
                            onTokenChange={setTurnstileToken}
                            onError={(message) => toast.error(message)}
                          />
                        </div>
                      )}
                    </motion.div>
                  )}
                </form>
              </motion.div>

              <div className="shrink-0 border-t border-slate-100 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-6 lg:px-12 lg:py-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
                  <button
                    type="button"
                    onClick={handleClearForm}
                    disabled={isPending}
                    className="w-full cursor-pointer rounded-xl px-6 py-3 text-center text-base font-bold text-slate-400 transition-colors hover:text-slate-900 disabled:opacity-50 sm:w-auto sm:py-4">
                    Clear
                  </button>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePreviousStep}
                      className="w-full cursor-pointer rounded-xl border border-slate-200 px-8 py-3 text-base font-bold text-slate-600 transition-all duration-300 hover:border-slate-300 hover:text-slate-900 sm:w-auto sm:py-4">
                      Back
                    </button>
                  )}

                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      disabled={!canProceedByStep[currentStep]}
                      className="group flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#1D4ED8] px-8 py-3 text-base font-bold text-white shadow-[0_8px_20px_-6px_rgba(29,78,216,0.5)] transition-all duration-300 hover:bg-[#1E40AF] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:py-4">
                      Next Step
                      <ArrowRight
                        size={18}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      form="demo-form"
                      disabled={
                        isPending || !canProceedByStep[4] || (isTurnstileEnabled && !turnstileToken)
                      }
                      className="group flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#1D4ED8] px-8 py-3 text-base font-bold text-white shadow-[0_8px_20px_-6px_rgba(29,78,216,0.5)] transition-all duration-300 hover:bg-[#1E40AF] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:py-4">
                      {isPending ? "Submitting..." : "Submit Request"}
                      {isPending ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <ArrowRight
                          size={18}
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </DialogContent>
      </Dialog>
      <ThankYouPage
        isVisible={showSuccess}
        onClose={handleSuccessClose}
        firstName={formData.firstName}
        preferredDate={formData.preferredDate}
        preferredTime={formData.preferredTime}
      />
    </>
  );
}
